import { Pill, SeverityCapsule } from "../../components/ui";
import { MediaBadge } from "../../components/MediaIndicator";
import { EMESRT_COLOR, fmtTime, fmtNum } from "../format";
import type { FieldDef, ReferenceDef, ResourceConfig } from "./resources";
import type { Severity } from "../../types/api";

// Capabilities (mirror backend apps/accounts/rbac.py) that gate writes per resource.
const PLATFORM = "platform.manage" as const; // platform admins (super_admin)
const CONFIG = "config.write" as const; // tenant config writers (integrator+)
const APIKEY = "apikey.manage" as const; // developer key management (integrator+)
const EXT_ID_HELP = "Auto-assigned if left blank";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const bool = (v: any) => (
  <Pill color={v ? "#2DD4BF" : "#64748B"}>{v ? "active" : "inactive"}</Pill>
);

/** Concise builder for a searchable entity picker (stores an id, shows a name). */
const ref = (
  name: string,
  label: string,
  reference: ReferenceDef,
  opts: { required?: boolean; help?: string } = {}
): FieldDef => ({ name, label, type: "reference", reference, required: opts.required, help: opts.help });

/** An external / business identifier the server auto-assigns when omitted. */
const extId = (label = "External ID"): FieldDef => ({
  name: "ext_id", label, type: "number", help: EXT_ID_HELP,
});

const sla = [
  { value: "seconds", label: "Seconds (site-critical)" },
  { value: "business", label: "Business hours" },
  { value: "24x7", label: "24/7" },
];
const severityOpts = ["advisory", "low", "medium", "high", "critical"].map((s) => ({ value: s, label: s }));
const emesrtOpts = ["L7", "L8", "L9"].map((s) => ({ value: s, label: s }));
const personaOpts = [
  { value: "super_admin", label: "Super Admin" },
  { value: "integrator", label: "Technical Integrator" },
  { value: "site_manager", label: "Site Manager" },
];

export const RESOURCES: ResourceConfig[] = [
  // ======================================================= PLATFORM ADMINISTRATION
  {
    key: "tenants", label: "Tenants", singular: "Tenant", endpoint: "tenants",
    section: "platform", group: "Organization", icon: "building", titleField: "name",
    auditLabel: "Tenant", capability: PLATFORM, searchable: true,
    columns: [
      { key: "name", label: "Name", sortable: true },
      { key: "slug", label: "Slug" },
      { key: "sla_tier", label: "SLA", render: (r) => <Pill color="#3B82F6">{r.sla_tier}</Pill> },
      { key: "region_label", label: "Region" },
      { key: "sites_count", label: "Sites" },
      { key: "users_count", label: "Users" },
      { key: "enabled", label: "Status", render: (r) => bool(r.enabled) },
    ],
    fields: [
      { name: "slug", label: "Slug", type: "text", required: true, help: "unique key" },
      { name: "name", label: "Name", type: "text", required: true },
      { name: "region_label", label: "Region", type: "text" },
      { name: "sla_tier", label: "SLA tier", type: "select", options: sla, required: true },
      { name: "accent", label: "Accent colour", type: "text", placeholder: "#22D3EE" },
      { name: "enabled", label: "Enabled", type: "boolean" },
    ],
  },
  {
    key: "users", label: "Users", singular: "User", endpoint: "users",
    section: "platform", group: "Users & Roles", icon: "user", titleField: "username",
    auditLabel: "User", capability: PLATFORM, searchable: true,
    columns: [
      { key: "username", label: "Username", sortable: true },
      { key: "email", label: "Email" },
      { key: "persona", label: "System role", render: (r) => <Pill color="#3B82F6">{r.persona}</Pill> },
      { key: "home_tenant_name", label: "Home tenant" },
      { key: "is_active", label: "Status", render: (r) => bool(r.is_active) },
    ],
    fields: [
      { name: "username", label: "Username / email", type: "text", required: true },
      { name: "email", label: "Email", type: "text" },
      { name: "first_name", label: "First name", type: "text" },
      { name: "last_name", label: "Last name", type: "text" },
      { name: "persona", label: "System role", type: "select", options: personaOpts, required: true,
        help: "governs capabilities in every tenant" },
      { name: "title", label: "Title", type: "text" },
      ref("home_tenant", "Home tenant", { endpoint: "tenants", optionLabel: "name", labelFrom: "home_tenant_name" }),
      { name: "password", label: "Password", type: "password", help: "blank = unchanged" },
      { name: "is_active", label: "Active", type: "boolean" },
    ],
    relations: [
      { label: "Memberships", endpoint: "memberships", filterParam: "user",
        columns: [{ key: "tenant_name", label: "Tenant" }, { key: "access_all_sites", label: "All sites", render: (r) => bool(r.access_all_sites) }] },
    ],
  },
  {
    key: "memberships", label: "Tenant Access", singular: "Membership", endpoint: "memberships",
    section: "platform", group: "Users & Roles", icon: "shield", titleField: "user_name",
    auditLabel: "TenantMembership", capability: PLATFORM,
    columns: [
      { key: "user_name", label: "User", sortable: true },
      { key: "tenant_name", label: "Tenant" },
      { key: "access_all_sites", label: "All sites", render: (r) => bool(r.access_all_sites) },
    ],
    fields: [
      ref("user", "User", { endpoint: "users", optionLabel: "username", optionSublabel: "email", labelFrom: "user_name" }, { required: true }),
      ref("tenant", "Tenant", { endpoint: "tenants", optionLabel: "name", labelFrom: "tenant_name" }, { required: true }),
      { name: "access_all_sites", label: "Access all sites in tenant", type: "boolean",
        help: "otherwise limited to sites granted below" },
    ],
  },
  {
    key: "site-permissions", label: "Site Permissions", singular: "Permission", endpoint: "site-permissions",
    section: "platform", group: "Users & Roles", icon: "key", titleField: "user_name",
    auditLabel: "SitePermission", capability: PLATFORM,
    columns: [
      { key: "user_name", label: "User", sortable: true },
      { key: "site_title", label: "Site" },
      { key: "level", label: "Level", render: (r) => <Pill color="#22D3EE">{r.level}</Pill> },
    ],
    fields: [
      ref("user", "User", { endpoint: "users", optionLabel: "username", optionSublabel: "email", labelFrom: "user_name" }, { required: true }),
      ref("site", "Site", { endpoint: "sites", optionLabel: "title", labelFrom: "site_title" }, { required: true }),
      { name: "level", label: "Level", type: "select", required: true, options: [
        { value: "view", label: "View" }, { value: "triage", label: "Triage / classify" }, { value: "manage", label: "Manage / configure" }] },
    ],
  },
  {
    key: "asset-taxonomies", label: "Asset Taxonomies", singular: "Taxonomy", endpoint: "asset-taxonomies",
    section: "platform", group: "Reference Data", icon: "grid", titleField: "label",
    auditLabel: "AssetTaxonomy", capability: PLATFORM, searchable: true,
    columns: [
      { key: "code", label: "Code", sortable: true },
      { key: "label", label: "Label", sortable: true },
      { key: "category", label: "Category" },
      { key: "nominal_height_m", label: "Height (m)" },
      { key: "icon", label: "Icon" },
    ],
    fields: [
      { name: "code", label: "Code", type: "text", required: true, help: "unique slug, e.g. haul_truck" },
      { name: "label", label: "Label", type: "text", required: true },
      { name: "category", label: "Category", type: "text", placeholder: "mobile_plant" },
      { name: "nominal_height_m", label: "Nominal height (m)", type: "number", step: 0.1 },
      { name: "icon", label: "Icon", type: "text", placeholder: "truck" },
    ],
  },
  {
    key: "event-types", label: "Event Types", singular: "Event Type", endpoint: "event-types",
    section: "platform", group: "Reference Data", icon: "alert", titleField: "label",
    auditLabel: "EventType", capability: PLATFORM, searchable: true,
    columns: [
      { key: "code", label: "Alarm", sortable: true },
      { key: "label", label: "Label" },
      { key: "default_severity", label: "Severity", render: (r) => <SeverityCapsule severity={r.default_severity as Severity} /> },
      { key: "emesrt_level", label: "EMESRT", render: (r) => <Pill color={EMESRT_COLOR[r.emesrt_level] ?? "#64748B"}>{r.emesrt_level}</Pill> },
    ],
    fields: [
      { name: "code", label: "Alarm code", type: "number", help: EXT_ID_HELP },
      { name: "slug", label: "Slug", type: "text", required: true },
      { name: "label", label: "Label", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "default_severity", label: "Default severity", type: "select", options: severityOpts, required: true },
      { name: "emesrt_level", label: "EMESRT level", type: "select", options: emesrtOpts, required: true },
      { name: "advisory_zone_m", label: "Advisory zone (m)", type: "number", step: 0.1 },
      { name: "red_zone_m", label: "Red zone (m)", type: "number", step: 0.1 },
      { name: "intervention_default", label: "Default intervention", type: "text" },
    ],
  },
  {
    key: "risk-matrix", label: "Risk Matrix", singular: "Matrix Entry", endpoint: "risk-matrix",
    section: "platform", group: "Reference Data", icon: "grid", titleField: "consequence",
    auditLabel: "RiskMatrixEntry", capability: PLATFORM, supportsBulk: false,
    columns: [
      { key: "consequence", label: "Consequence", sortable: true },
      { key: "likelihood", label: "Likelihood" },
      { key: "severity_class", label: "Severity", render: (r) => <SeverityCapsule severity={r.severity_class as Severity} /> },
      { key: "color", label: "Colour", render: (r) => <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded" style={{ background: r.color }} />{r.color}</span> },
    ],
    fields: [
      { name: "consequence", label: "Consequence", type: "text", required: true },
      { name: "likelihood", label: "Likelihood", type: "text", required: true },
      { name: "severity_class", label: "Severity class", type: "select", options: severityOpts, required: true },
      { name: "color", label: "Colour", type: "text", placeholder: "#F97316" },
    ],
  },
  {
    key: "models", label: "AI Models", singular: "Model", endpoint: "models",
    section: "platform", group: "Reference Data", icon: "brain", titleField: "name",
    auditLabel: "MLModel", capability: PLATFORM, searchable: true,
    columns: [
      { key: "name", label: "Model", sortable: true },
      { key: "version", label: "Version" },
      { key: "status", label: "Status", render: (r) => <Pill color={r.status === "prod" ? "#2DD4BF" : r.status === "canary" ? "#F59E0B" : "#64748B"}>{r.status}</Pill> },
      { key: "accuracy", label: "Accuracy", render: (r) => `${(r.accuracy * 100).toFixed(1)}%` },
      { key: "fp_rate", label: "FP-rate", render: (r) => `${(r.fp_rate * 100).toFixed(1)}%` },
      { key: "latency_ms", label: "Latency", render: (r) => `${r.latency_ms}ms` },
    ],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "version", label: "Version", type: "text", required: true },
      { name: "status", label: "Status", type: "select", required: true, options: [
        { value: "training", label: "Training" }, { value: "canary", label: "Canary" },
        { value: "prod", label: "Production" }, { value: "retired", label: "Retired" }] },
      { name: "accuracy", label: "Accuracy (0-1)", type: "number", step: 0.001 },
      { name: "fp_rate", label: "FP rate (0-1)", type: "number", step: 0.001 },
      { name: "drift_30d", label: "Drift 30d (%)", type: "number", step: 0.1 },
      { name: "latency_ms", label: "Latency (ms)", type: "number" },
      { name: "canary_traffic_pct", label: "Canary traffic %", type: "number" },
      { name: "task", label: "Task", type: "text" },
    ],
  },

  // ======================================================= TENANT ADMINISTRATION
  {
    key: "sites", label: "Sites", singular: "Site", endpoint: "sites",
    section: "tenant", group: "Sites & Assets", icon: "pin", titleField: "title",
    auditLabel: "Site", capability: CONFIG, searchable: true,
    columns: [
      { key: "title", label: "Site", sortable: true },
      { key: "ext_id", label: "Ext ID" },
      { key: "asset_count", label: "Assets" },
      { key: "latitude", label: "Lat", render: (r) => (r.latitude ? r.latitude.toFixed(3) : "—") },
      { key: "longitude", label: "Lng", render: (r) => (r.longitude ? r.longitude.toFixed(3) : "—") },
    ],
    fields: [
      { name: "title", label: "Title", type: "text", required: true },
      extId(),
      { name: "latitude", label: "Latitude", type: "number", step: 0.0001 },
      { name: "longitude", label: "Longitude", type: "number", step: 0.0001 },
    ],
    relations: [
      { label: "Assets", endpoint: "assets", filterParam: "site",
        columns: [{ key: "registration", label: "Asset" }, { key: "machine_type", label: "Type" }] },
      { label: "Personnel", endpoint: "personnel", filterParam: "site",
        columns: [{ key: "name", label: "Name" }, { key: "role", label: "Role" }] },
      { label: "Exceptions", endpoint: "events", filterParam: "site",
        columns: [
          { key: "event_type_label", label: "Type" },
          { key: "severity", label: "Sev", render: (r) => <SeverityCapsule severity={r.severity as Severity} /> },
          { key: "media", label: "Media", render: (r) => (r.media ? <MediaBadge media={r.media} /> : "—") },
        ] },
    ],
  },
  {
    key: "assets", label: "Assets", singular: "Asset", endpoint: "assets",
    section: "tenant", group: "Sites & Assets", icon: "truck", titleField: "registration",
    auditLabel: "Asset", capability: CONFIG, searchable: true,
    columns: [
      { key: "registration", label: "Registration", sortable: true },
      { key: "machine_type", label: "Machine type", sortable: true },
      { key: "taxonomy_label", label: "Taxonomy" },
      { key: "site_title", label: "Site" },
    ],
    fields: [
      { name: "registration", label: "Registration", type: "text", required: true },
      { name: "machine_type", label: "Machine type", type: "text" },
      ref("taxonomy", "Taxonomy", { endpoint: "asset-taxonomies", optionLabel: "label", labelFrom: "taxonomy_label" }),
      ref("site", "Site", { endpoint: "sites", optionLabel: "title", labelFrom: "site_title" }),
      extId(),
    ],
    relations: [
      { label: "Devices", endpoint: "devices", filterParam: "asset",
        columns: [{ key: "ext_id", label: "Device" }, { key: "soc", label: "SoC" }] },
    ],
  },
  {
    key: "devices", label: "Devices", singular: "Device", endpoint: "devices",
    section: "tenant", group: "Sites & Assets", icon: "cpu", titleField: "ext_id",
    auditLabel: "Device", capability: CONFIG,
    columns: [
      { key: "ext_id", label: "Device", sortable: true },
      { key: "asset_registration", label: "Asset" },
      { key: "soc", label: "SoC" },
      { key: "carrier", label: "Carrier" },
      { key: "edge_agent_version", label: "Agent" },
      { key: "online", label: "Status", render: (r) => <Pill color={r.online ? "#2DD4BF" : "#64748B"}>{r.online ? "online" : "offline"}</Pill> },
    ],
    fields: [
      ref("asset", "Asset", { endpoint: "assets", optionLabel: "registration", labelFrom: "asset_registration" }),
      { name: "soc", label: "SoC", type: "text" },
      { name: "carrier", label: "Carrier", type: "text" },
      { name: "edge_agent_version", label: "Edge agent version", type: "text" },
      { name: "enabled", label: "Enabled", type: "boolean" },
      { name: "capability_flags", label: "Capability flags", type: "json", help: "JSON object" },
      extId(),
    ],
  },
  {
    key: "channels", label: "Channels", singular: "Channel", endpoint: "channels",
    section: "tenant", group: "Sites & Assets", icon: "cpu", titleField: "label",
    auditLabel: "Channel", capability: CONFIG, searchable: true,
    columns: [
      { key: "label", label: "Channel", sortable: true },
      { key: "ext_id", label: "Ext ID" },
    ],
    fields: [
      { name: "label", label: "Label", type: "text", required: true },
      extId(),
    ],
  },
  {
    key: "detection-zones", label: "Detection Zones", singular: "Zone", endpoint: "detection-zones",
    section: "tenant", group: "Detection & Personnel", icon: "radar", titleField: "name",
    auditLabel: "DetectionZone", capability: CONFIG, searchable: true,
    columns: [
      { key: "name", label: "Zone", sortable: true },
      { key: "taxonomy_label", label: "Taxonomy" },
      { key: "red_zone_m", label: "Red (m)" },
      { key: "advisory_zone_m", label: "Advisory (m)" },
      { key: "emesrt_level", label: "EMESRT", render: (r) => <Pill color={EMESRT_COLOR[r.emesrt_level] ?? "#64748B"}>{r.emesrt_level}</Pill> },
      { key: "auto_intervention", label: "Auto", render: (r) => bool(r.auto_intervention) },
    ],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      ref("taxonomy", "Taxonomy", { endpoint: "asset-taxonomies", optionLabel: "label", labelFrom: "taxonomy_label" }),
      { name: "red_zone_m", label: "Red exclusion zone (m)", type: "number", step: 0.1, required: true },
      { name: "advisory_zone_m", label: "Advisory zone (m)", type: "number", step: 0.1, required: true },
      { name: "emesrt_level", label: "EMESRT level", type: "select", options: emesrtOpts, required: true },
      { name: "auto_intervention", label: "Auto intervention", type: "boolean" },
    ],
  },
  {
    key: "personnel", label: "Personnel", singular: "Person", endpoint: "personnel",
    section: "tenant", group: "Detection & Personnel", icon: "users", titleField: "name",
    auditLabel: "Personnel", capability: CONFIG, searchable: true,
    columns: [
      { key: "name", label: "Name", sortable: true },
      { key: "role", label: "Role" },
      { key: "site_title", label: "Site" },
      { key: "shift", label: "Shift" },
      { key: "status", label: "Status", render: (r) => <Pill color={r.status === "on_shift" ? "#2DD4BF" : "#64748B"}>{r.status}</Pill> },
      { key: "clean_shift_days", label: "Clean days" },
    ],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "role", label: "Role", type: "text" },
      { name: "shift", label: "Shift", type: "select", options: [{ value: "Day", label: "Day" }, { value: "Night", label: "Night" }] },
      { name: "status", label: "Status", type: "select", options: [
        { value: "on_shift", label: "On shift" }, { value: "off_shift", label: "Off shift" }, { value: "break", label: "On break" }] },
      { name: "clean_shift_days", label: "Clean-shift days", type: "number" },
      ref("site", "Site", { endpoint: "sites", optionLabel: "title", labelFrom: "site_title" }),
    ],
  },
  {
    key: "webhooks", label: "Webhooks", singular: "Webhook", endpoint: "webhooks",
    section: "tenant", group: "Developer", icon: "webhook", titleField: "url",
    auditLabel: "Webhook", capability: CONFIG,
    columns: [
      { key: "url", label: "Endpoint", sortable: true },
      { key: "events", label: "Events", render: (r) => (r.events || []).join(", ") },
      { key: "enabled", label: "Status", render: (r) => bool(r.enabled) },
      { key: "last_delivery_status", label: "Last delivery" },
    ],
    fields: [
      { name: "url", label: "Endpoint URL", type: "text", required: true },
      { name: "events", label: "Events", type: "json", help: 'e.g. ["exception.created"]' },
      { name: "secret", label: "Signing secret", type: "text" },
      { name: "enabled", label: "Enabled", type: "boolean" },
    ],
  },
  {
    key: "api-keys", label: "API Keys", singular: "API Key", endpoint: "api-keys",
    section: "tenant", group: "Developer", icon: "key", titleField: "name",
    auditLabel: "ApiKey", capability: APIKEY, noEdit: true, supportsBulk: false,
    columns: [
      { key: "name", label: "Name", sortable: true },
      { key: "prefix", label: "Prefix", render: (r) => <span className="mono">{r.prefix}…</span> },
      { key: "scopes", label: "Scopes", render: (r) => (r.scopes || []).join(", ") },
      { key: "last_used_at", label: "Last used", render: (r) => (r.last_used_at ? fmtTime(r.last_used_at) : "never") },
      { key: "revoked", label: "Status", render: (r) => <Pill color={r.revoked ? "#EF4444" : "#2DD4BF"}>{r.revoked ? "revoked" : "active"}</Pill> },
    ],
    fields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "scopes", label: "Scopes", type: "json", help: 'e.g. ["incidents:read"]' },
    ],
  },
  {
    key: "exports", label: "Export Jobs", singular: "Export", endpoint: "exports",
    section: "tenant", group: "Audit & Exports", icon: "download", titleField: "title",
    auditLabel: "ExportJob", readOnly: true, supportsBulk: false,
    columns: [
      { key: "title", label: "Export", sortable: true },
      { key: "kind", label: "Kind" },
      { key: "fmt", label: "Format", render: (r) => <span className="mono uppercase">{r.fmt}</span> },
      { key: "row_count", label: "Rows", render: (r) => (r.row_count ? fmtNum(r.row_count) : "—") },
      { key: "status", label: "Status", render: (r) => <Pill color={r.status === "ready" ? "#2DD4BF" : r.status === "failed" ? "#EF4444" : "#F59E0B"}>{r.status}</Pill> },
      { key: "created_at", label: "Created", render: (r) => fmtTime(r.created_at) },
    ],
    fields: [],
  },
];

export const SECTIONS: { key: "platform" | "tenant"; label: string; groups: string[] }[] = [
  {
    key: "platform", label: "Platform Administration",
    groups: ["Organization", "Users & Roles", "Reference Data"],
  },
  {
    key: "tenant", label: "Tenant Administration",
    groups: ["Sites & Assets", "Detection & Personnel", "Developer", "Audit & Exports"],
  },
];

export function resourceByKey(key: string): ResourceConfig | undefined {
  return RESOURCES.find((r) => r.key === key);
}
